/**
 * atomberg-pdp-gallery-guard.js
 *
 * iOS Safari sometimes scrolls the PDP media-gallery carousel to a random
 * slide during its load window (snap-aligned positions, never on Chromium)
 * — a WebKit race between Horizon's slideshow initialization and
 * scroll-snap layout while lazy gallery images stream in. The server
 * markup is correct (initial-slide="0"), so this guard holds the track at
 * its intended initial slide until the user first interacts with the
 * gallery or the load window passes, then permanently disarms — for THAT
 * element.
 *
 * assets/media-gallery.js replaces the entire <media-gallery> element with
 * a fresh one on every variant change (#handleVariantUpdate ->
 * this.replaceWith(newMediaGallery)) — a brand-new custom element runs
 * the exact same slideshow init from scratch, so it's exposed to the exact
 * same race. A gallery is armed per-element (not a single page-wide flag),
 * and a MutationObserver re-arms every replacement gallery automatically —
 * this is the fix for the long-standing "random image after switching
 * variant" report, which the previous once-per-page version of this guard
 * never covered.
 *
 * It can never fight a user-driven scroll: scrolling the carousel requires
 * a gesture on the gallery first (touchstart/pointerdown/wheel/keydown, or
 * focusin for keyboard tabbing), and those disarm the guard in capture
 * phase before the resulting scroll event ever fires. Only gesture-less
 * scrolls — the WebKit race — get corrected.
 */
(function () {
  if (/** @type {any} */ (window)._atombergPdpGalleryGuardInit) return;
  /** @type {any} */ (window)._atombergPdpGalleryGuardInit = true;

  /** Post-load settle window — WebKit's re-snap tracks image loading. */
  var AFTER_LOAD_MS = 3000;
  /** Hard cap from arm time in case 'load' never fires (or fires late on a
   * slow initial load — a variant-swap gallery is always armed well after
   * 'load', so this branch never applies there). */
  var HARD_CAP_MS = 10000;

  /** @param {HTMLElement} gallery */
  function arm(gallery) {
    if (gallery.dataset.atombergGalleryGuardArmed) return;
    gallery.dataset.atombergGalleryGuardArmed = 'true';

    var track = gallery.querySelector('slideshow-slides');
    if (!track) return;
    var trackEl = /** @type {HTMLElement} */ (track);

    var slideshow = trackEl.closest('slideshow-component');
    var initialIndex = 0;
    if (slideshow) {
      initialIndex = parseInt(slideshow.getAttribute('initial-slide') || '0', 10) || 0;
    }

    var armed = true;
    var deadline = Date.now() + HARD_CAP_MS;

    function expectedLeft() {
      var slides = trackEl.querySelectorAll('slideshow-slide');
      var target = /** @type {HTMLElement | undefined} */ (slides[initialIndex]);
      return target ? target.offsetLeft : 0;
    }

    function disarm() {
      armed = false;
    }

    function correct() {
      if (!armed) return;
      if (Date.now() > deadline) {
        disarm();
        return;
      }
      var want = expectedLeft();
      if (Math.abs(trackEl.scrollLeft - want) < 2) return;
      trackEl.scrollTo({ left: want, behavior: 'instant' });
    }

    // Any user intent on the gallery (swipe, wheel, arrow/thumbnail click,
    // keyboard, tab-focus into a slide) means scrolls are legitimate from
    // here on. Capture phase so we disarm before the gesture's own scroll.
    ['pointerdown', 'touchstart', 'wheel', 'keydown', 'focusin'].forEach(function (type) {
      gallery.addEventListener(type, disarm, { capture: true, passive: true });
    });

    trackEl.addEventListener('scroll', correct);

    // On initial page load, shrink the hard cap once 'load' fires (bounded
    // extra runway for images still decoding). A variant-swap gallery is
    // always armed with readyState already 'complete', so this branch is
    // page-load-only and the hard cap alone governs the swap case.
    if (document.readyState === 'complete') {
      correct();
    } else {
      window.addEventListener(
        'load',
        function () {
          deadline = Math.min(deadline, Date.now() + AFTER_LOAD_MS);
          correct();
        },
        { once: true }
      );
    }

    // The rogue jump may predate arming (slow networks, or the variant-swap
    // response rendering before this observer callback runs) — fix any
    // deviation that already happened.
    correct();
  }

  /** @param {ParentNode} root */
  function armAllIn(root) {
    if (!(root instanceof Element) && !(root instanceof Document)) return;
    if (root instanceof Element && root.matches('media-gallery')) {
      arm(/** @type {HTMLElement} */ (root));
    }
    root.querySelectorAll('media-gallery').forEach(function (el) {
      arm(/** @type {HTMLElement} */ (el));
    });
  }

  function init() {
    armAllIn(document);

    // Covers every future replacement (variant switch today, whatever else
    // tomorrow) without depending on any specific Horizon event name.
    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType === 1) armAllIn(/** @type {Element} */ (node));
        });
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
